import styles from '@pages/TemplateEditor/components/RenderNode.module.scss';

const STYLE_MAP = {
  fontWeight: {
    cssKey: 'fontWeight',
    value: 'bold',
    removeCssKey: 'font-weight',
  },
  fontItalic: {
    cssKey: 'fontStyle',
    value: 'italic',
    removeCssKey: 'font-style',
  },
  textDecoration: {
    cssKey: 'textDecoration',
    value: 'underline',
    removeCssKey: 'text-decoration',
  },
};

const DEFAULT_STYLES = {
  fontWeight: false,
  fontItalic: false,
  textDecoration: false,
};

const isNodeText = (node) => {
  return node?.nodeType === Node.TEXT_NODE;
};

const isElementSpan = (elementNode) => {
  return elementNode?.tagName === 'SPAN';
};

const isElementBrTag = (elementNode) => {
  return elementNode?.tagName === 'BR';
};

const isElementAndSpan = (elementNode) => {
  return (
    elementNode.nodeType === Node.ELEMENT_NODE &&
    elementNode?.tagName === 'SPAN'
  );
};

function isSpanExplicitlyNonEditable(node) {
  return (
    node?.nodeType === Node.ELEMENT_NODE &&
    node.tagName === 'SPAN' &&
    (node.getAttribute('contenteditable') || '').toLowerCase() === 'false'
  );
}

const isSpanEditable = (span) => {
  return !isSpanExplicitlyNonEditable(span);
};

const getEditorElement = () => {
  return document.querySelector('.craft-221');
};

const addListenerToCraftEditor = (handler) => {
  document.querySelector('.craft-221')?.addEventListener('scroll', handler);
  window.addEventListener('resize', handler);
};

const removeListenerFromCraftEditor = (handler) => {
  document.querySelector('.craft-221')?.removeEventListener('scroll', handler);
  window.removeEventListener('resize', handler);
};

const isElementNode = (node) => {
  return node?.nodeType === Node.ELEMENT_NODE;
};

const getElementNode = (range) => {
  return isElementNode(range.commonAncestorContainer)
    ? range.commonAncestorContainer
    : range.commonAncestorContainer.parentElement;
};

const createSpanEl = () => {
  return document.createElement('span');
};

const isNothingSelected = (selection) => {
  return selection.rangeCount === 0;
};

const getSelectionAndRange = () => {
  const editorEl = getEditorElement();
  const selection = window.getSelection?.();
  if (!selection || isNothingSelected(selection))
    return { selection: undefined, range: undefined };

  const range = selection.getRangeAt(0);

  if (!editorEl.contains(range.commonAncestorContainer))
    return { selection: undefined, range: undefined };

  return { selection, range };
};

const getFirstElementNode = (parentNode) => {
  return (
    parentNode &&
    (isElementNode(parentNode) ? parentNode : parentNode.parentElement)
  );
};

const isNextSiblingEditableSpan = (currentNode) => {
  return (
    currentNode.nextSibling &&
    currentNode.nextSibling.nodeType === Node.ELEMENT_NODE &&
    isElementSpan(currentNode.nextSibling) &&
    isSpanEditable(currentNode.nextSibling)
  );
};

const isElementStyleItalic = (elStyle) => {
  return elStyle.fontStyle === 'italic';
};

const isElementStyleBold = (elStyle) => {
  const fontWeight = elStyle.fontWeight;
  return fontWeight === 'bold' || parseInt(fontWeight, 10) >= 600;
};

const isElementStyleUnderline = (elStyle) => {
  const deco = elStyle.textDecorationLine || elStyle.textDecoration || '';
  return deco.includes('underline');
};

const getElementStyleValues = (elementNode) => {
  return window.getComputedStyle(elementNode);
};

const isElementInRangeOfParagraph = (elementNode) => {
  return elementNode.tagName !== 'P' && elementNode.tagName !== 'BODY';
};

const propagateToParentNode = (elementNode) => {
  return isElementNode(elementNode) && isElementInRangeOfParagraph(elementNode);
};

const getParentElement = (elementNode) => {
  return elementNode.parentElement;
};

const getElementDecoration = (elementNode) => {
  let isBold = false;
  let isItalic = false;
  let isUnderline = false;

  while (propagateToParentNode(elementNode)) {
    const elStyle = getElementStyleValues(elementNode);

    isBold = isBold || isElementStyleBold(elStyle);
    isItalic = isItalic || isElementStyleItalic(elStyle);
    isUnderline = isUnderline || isElementStyleUnderline(elStyle);

    if (isBold && isItalic && isUnderline) break;
    elementNode = getParentElement(elementNode);
  }

  return { isBold, isItalic, isUnderline };
};

const cleanUpSimilarSpans = (rootP) => {
  const walker = document.createTreeWalker(rootP, NodeFilter.SHOW_ELEMENT);
  let currentNode;
  while ((currentNode = walker.nextNode())) {
    if (
      isElementSpan(currentNode) &&
      isSpanEditable(currentNode) &&
      isNextSiblingEditableSpan(currentNode)
    ) {
      const nextSiblingNode = currentNode.nextSibling;
      if (
        currentNode.getAttribute('style') ===
        nextSiblingNode.getAttribute('style')
      ) {
        while (nextSiblingNode.firstChild)
          currentNode.appendChild(nextSiblingNode.firstChild);
        nextSiblingNode.remove();
        walker.currentNode = currentNode;
      }
    }
  }
};

const checkStyleDeclarations = (elementNode) => {
  return elementNode.style && elementNode.style.length > 0;
};

const getStyleAttribute = (elementNode) => {
  return elementNode.getAttribute('style');
};

const checkNonEmptyStyleAttribute = (currentSpan) => {
  const rawStyleAttr = getStyleAttribute(currentSpan);
  return typeof rawStyleAttr === 'string' && rawStyleAttr.trim() !== '';
};

const removeEmptySpans = (rootP) => {
  rootP.querySelectorAll('span').forEach((currentSpan) => {
    const hasClass = currentSpan.classList && currentSpan.classList.length > 0;

    const isStyleDecls = checkStyleDeclarations(currentSpan);
    const isNonEmptyStyleAttr = checkNonEmptyStyleAttribute(currentSpan);

    const hasStyle = isStyleDecls || isNonEmptyStyleAttr;

    if (!hasClass && !hasStyle) {
      const currentSpanParent = currentSpan.parentNode;
      while (currentSpan.firstChild)
        currentSpanParent.insertBefore(currentSpan.firstChild, currentSpan);
      currentSpanParent.removeChild(currentSpan);
    } else {
      if (!isStyleDecls && currentSpan.hasAttribute('style')) {
        currentSpan.removeAttribute('style');
      }
    }
  });
};

const inheritCEFalse = (el) =>
  el && el.getAttribute && el.getAttribute('contenteditable') === 'false';

const addClassesFromOneElementToAnother = (fromEl, toEl) => {
  if (!(toEl instanceof Element)) return;

  fromEl.classList.forEach((currentClass) => toEl.classList.add(currentClass));
};

const flattenNestedSpans = (rootP) => {
  const allNestedSpans = [...rootP.querySelectorAll('span span')];
  allNestedSpans.forEach((nestedSpan) => {
    const nestedSpanParent = nestedSpan.parentElement;

    if (!isElementSpan(nestedSpanParent)) return;

    addClassesFromOneElementToAnother(nestedSpanParent, nestedSpan);

    const parentStyle = nestedSpanParent.getAttribute('style');
    if (parentStyle) {
      const childStyle = getStyleAttribute(nestedSpan) || '';
      nestedSpan.setAttribute(
        'style',
        [childStyle, parentStyle].filter(Boolean).join(';')
      );
    }
    const parentHadCEFalse = inheritCEFalse(nestedSpanParent);
    if (parentHadCEFalse && !nestedSpan.hasAttribute('contenteditable')) {
      nestedSpan.setAttribute('contenteditable', 'false');
    }

    nestedSpanParent.replaceWith(nestedSpanParent);
    nestedSpanParent.parentNode.insertBefore(nestedSpan, nestedSpanParent);

    [...nestedSpanParent.childNodes].forEach((childNode) => {
      if (isNodeText(childNode)) {
        const span = createSpanEl();
        addClassesFromOneElementToAnother(nestedSpanParent, span);

        if (parentStyle) {
          span.setAttribute('style', parentStyle);
        }

        span.textContent = childNode.nodeValue;
        nestedSpanParent.parentNode.insertBefore(span, nestedSpanParent);
      } else {
        if (childNode.classList) {
          addClassesFromOneElementToAnother(nestedSpanParent, childNode);
        }

        if (parentStyle && childNode.setAttribute) {
          const nodeStyle = childNode.getAttribute('style') || '';
          childNode.setAttribute(
            'style',
            [nodeStyle, parentStyle].filter(Boolean).join(';')
          );
        }

        nestedSpanParent.parentNode.insertBefore(childNode, nestedSpanParent);
      }
    });
    nestedSpanParent.remove();
  });
};

const normalizeNode = (rootP) => {
  cleanUpSimilarSpans(rootP);
  flattenNestedSpans(rootP);
  removeEmptySpans(rootP);
};

const isNoRange = (range) => {
  return !range || range.collapsed;
};

function isWhitespaceText(node) {
  return node && isNodeText(node) && !node.nodeValue.trim();
}

const applyInlineStyle = ({ elementNode, fieldName, isActive }) => {
  const { cssKey, value, removeCssKey } = STYLE_MAP[fieldName];
  if (isActive) {
    elementNode?.style?.removeProperty(removeCssKey);
  } else {
    elementNode.style[cssKey] = value;
  }
};

const isNodeParentAnchor = (node) => {
  return node.parentElement?.closest('a');
};

const createTreeWalkerForP = (rootP, range) => {
  return document.createTreeWalker(
    rootP,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    {
      acceptNode(node) {
        if (isElementSpan(node)) return NodeFilter.FILTER_ACCEPT;
        if (isWhitespaceText(node)) return NodeFilter.FILTER_SKIP;
        if (isNodeParentAnchor(node)) return NodeFilter.FILTER_ACCEPT;
        if (node == rootP) return NodeFilter.FILTER_SKIP;
        if (isElementBrTag(node)) return NodeFilter.FILTER_REJECT;
        return isNodeIntersectSelection(node, range)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    }
  );
};

const isFullyInRange = (node, range) => {
  if (range.containsNode) return range.containsNode(node, false);
  const newRange = document.createRange();
  isNodeText(node)
    ? newRange.selectNodeContents(node)
    : newRange.selectNode(node);
  const startsBeforeOrAt =
    range.compareBoundaryPoints(Range.START_TO_START, newRange) <= 0;
  const endsAfterOrAt =
    range.compareBoundaryPoints(Range.END_TO_END, newRange) >= 0;
  return startsBeforeOrAt && endsAfterOrAt;
};

const isNodeIntersectSelection = (node, range) => {
  if (range.intersectsNode) return range.intersectsNode(node);
  const newRange = document.createRange();
  isNodeText(node)
    ? newRange.selectNodeContents(node)
    : newRange.selectNode(node);
  return !(
    newRange.compareBoundaryPoints(Range.END_TO_START, range) <= 0 ||
    newRange.compareBoundaryPoints(Range.START_TO_END, range) >= 0
  );
};

const isSpanPartiallySelected = (span, range) => {
  if (!range || !span) return false;
  const inter =
    typeof range.intersectsNode === 'function'
      ? range.intersectsNode(span)
      : true;
  const full =
    typeof range.containsNode === 'function'
      ? range.containsNode(span, false)
      : false;
  return inter && !full;
};

const splitSpanForRange = (span, range) => {
  if (!isSpanPartiallySelected(span, range)) return null;

  span.normalize();

  const spanTextNode = span.firstChild;

  if (!spanTextNode || spanTextNode.nodeType !== Node.TEXT_NODE) {
    return;
  }

  const spanParent = span.parentNode;
  const textData = spanTextNode.data;

  const spanTextStartInRange = spanTextNode === range.startContainer;
  const spanTextEndInRange = spanTextNode === range.endContainer;

  function cloneEmptySpan() {
    const cloneSpan = span.cloneNode(false);
    return cloneSpan;
  }

  function replaceNodeWithReplacements(node, ...replacements) {
    const ref = node;
    replacements.forEach((n) => spanParent.insertBefore(n, ref));
    ref.remove();
  }

  if (spanTextStartInRange && spanTextEndInRange) {
    const rangeStartOffset = range.startOffset;
    const rangeEndOffset = range.endOffset;

    const leftText = textData.slice(0, rangeStartOffset);
    const midText = textData.slice(rangeStartOffset, rangeEndOffset);
    const rightText = textData.slice(rangeEndOffset);

    const leftSpan = leftText.length ? cloneEmptySpan() : null;
    const midSpan = midText.length ? cloneEmptySpan() : null;
    const rightSpan = rightText.length ? cloneEmptySpan() : null;

    if (leftSpan) leftSpan.textContent = leftText;
    if (midSpan) midSpan.textContent = midText;
    if (rightSpan) rightSpan.textContent = rightText;

    const created = [leftSpan, midSpan, rightSpan].filter(Boolean);
    if (created.length === 0) return null;

    replaceNodeWithReplacements(span, ...created);

    const selectedIndex = leftSpan ? 1 : 0;

    return { spans: created, selectedIndex };
  }

  if (spanTextStartInRange && !spanTextEndInRange) {
    const rangeStartOffset = range.startOffset;
    const leftText = textData.slice(0, rangeStartOffset);
    const selText = textData.slice(rangeStartOffset);

    const leftSpan = leftText.length ? cloneEmptySpan() : null;
    const selSpan = selText.length ? cloneEmptySpan() : null;

    if (leftSpan) leftSpan.textContent = leftText;
    if (selSpan) selSpan.textContent = selText;

    const created = [leftSpan, selSpan].filter(Boolean);
    if (created.length === 0) return null;

    replaceNodeWithReplacements(span, ...created);

    const selectedIndex = created.length === 2 ? 1 : 0;

    return { spans: created, selectedIndex };
  }

  if (spanTextEndInRange && !spanTextStartInRange) {
    const rangeEndOffset = range.endOffset;
    const selText = textData.slice(0, rangeEndOffset);
    const rightText = textData.slice(rangeEndOffset);

    const selSpan = selText.length ? cloneEmptySpan() : null;
    const rightSpan = rightText.length ? cloneEmptySpan() : null;

    if (selSpan) selSpan.textContent = selText;
    if (rightSpan) rightSpan.textContent = rightText;

    const created = [selSpan, rightSpan].filter(Boolean);
    if (created.length === 0) return null;

    replaceNodeWithReplacements(span, ...created);

    const selectedIndex = 0;

    return { spans: created, selectedIndex };
  }

  return null;
};

const getTextBoundariesOfSelectedNode = (textNode, range) => {
  const startTextBoundary =
    range.startContainer === textNode ? range.startOffset : 0;
  const endTextBoundary =
    range.endContainer === textNode ? range.endOffset : textNode.length;

  return { startTextBoundary, endTextBoundary };
};

const applyOrToggleStyle = (fieldName, isActive) => {
  const { range } = getSelectionAndRange();

  if (isNoRange(range)) return;

  const elementNode = getElementNode(range);
  const mainParagraphElement = elementNode?.closest('p');

  const walker = createTreeWalkerForP(mainParagraphElement, range);

  let currentNode;
  while ((currentNode = walker.nextNode())) {
    const isWholeElementSelected = isFullyInRange(currentNode, range);
    const isNodeSpan = isElementAndSpan(currentNode);

    if (isNodeSpan && isWholeElementSelected) {
      applyInlineStyle({ elementNode: currentNode, fieldName, isActive });
      continue;
    }

    if (isNodeSpan && !isWholeElementSelected) {
      const { spans, selectedIndex } =
        splitSpanForRange(currentNode, range) || {};
      if (!spans) continue;

      applyInlineStyle({
        elementNode: spans[selectedIndex],
        fieldName,
        isActive,
      });

      walker.currentNode = spans[spans.length - 1];
      continue;
    }

    if (!isNodeSpan) {
      const { startTextBoundary, endTextBoundary } =
        getTextBoundariesOfSelectedNode(currentNode, range);

      if (startTextBoundary === endTextBoundary) continue;

      if (endTextBoundary < currentNode.length) {
        currentNode.splitText(endTextBoundary);
      }

      let selectedText = currentNode;
      if (startTextBoundary > 0) {
        selectedText = currentNode.splitText(startTextBoundary);
      }

      const span = createSpanEl();
      applyInlineStyle({ elementNode: span, fieldName, isActive });
      selectedText.parentNode.insertBefore(span, selectedText);
      span.appendChild(selectedText);
      continue;
    }
  }

  normalizeNode(mainParagraphElement);
};

const getSelectionStyles = () => {
  const { selection, range } = getSelectionAndRange();
  if (!selection) return DEFAULT_STYLES;

  let elementNode = getFirstElementNode(range.startContainer);

  if (!elementNode) return DEFAULT_STYLES;

  const { isBold, isItalic, isUnderline } = getElementDecoration(elementNode);

  return {
    fontWeight: isBold,
    fontItalic: isItalic,
    textDecoration: isUnderline,
  };
};

const deleteSvg = `<svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1n4c0t4" focusable="false" aria-hidden="true" viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"></path></svg>
`;

const addDeleteButton = (nameEl, actions, id) => {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'icon-btn';
  btn.setAttribute('aria-label', 'Delete');
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    actions?.delete(id);
  });
  btn.innerHTML = deleteSvg;
  nameEl.insertAdjacentElement('beforeend', btn);
};

const getPos = (dom2) => {
  const { top, left, bottom, right } = dom2
    ? dom2?.getBoundingClientRect()
    : { top: 0, left: 0, bottom: 0 };

  return {
    topNum: top,
    top: `${top > 0 ? top - 2 : bottom}px`,
    left: `${left}px`,
    bottom: `${bottom}px`,
    right: `${right}px`,
  };
};

const createDivAndAddStyles = (className) => {
  const div = document.createElement('div');
  div.classList.add(className);
  return div;
};

const createIndicatorDiv = () => {
  const indicatorEl = createDivAndAddStyles(styles.indicatorDiv);

  return indicatorEl;
};

const createNameDiv = (name) => {
  let nameEl = createDivAndAddStyles(styles.nameDiv);
  nameEl.textContent = name;
  return nameEl;
};

const stateSelector = (node) => {
  if (!node) return {};
  return {
    isActive: node?.events?.selected,
    isHover: node?.events?.hovered,
    dom: node?.dom,
    name: node?.data.custom.displayName || node?.data.displayName,
    isDeletable: node?.data.props?.isDeletable,
  };
};

const addDeleteStyles = (dom) => {
  dom?.classList.add(styles.cellForDelete);
};

const removeDeleteStyles = (dom) => {
  dom?.classList.remove(styles.cellForDelete);
};

const addGridStyles = (dom) => {
  dom?.classList.add(styles.showGrid);
};

const removeGridStyles = (dom) => {
  dom?.classList.remove(styles.showGrid);
};

const removeSelectionStyles = (dom) => {
  dom?.classList.remove(styles.buttonSelected);
  dom?.classList.remove(styles.componentSelected);
};

const isDomButton = (dom) => {
  return dom?.localName === 'button';
};

const addButtonSelectionStyles = () => {
  return `${styles.buttonSelected}`;
};

const addComponentSelectionStyles = () => {
  return `${styles.componentSelected}`;
};

const updateElementPosition = (providedElement, rect) => {
  providedElement.style.top = rect.top;
  providedElement.style.left = rect.left;
};

const calcPageNumByHeight = (height, onePageHeight) => {
  return Math.ceil(height / onePageHeight);
};

const findNodeByName = ({ query, name }) => {
  const nodes = query.getNodes();

  for (const id in nodes) {
    if (nodes[id].data?.name === name) {
      return nodes[id];
    }
  }
  return null;
};

const cleanCalcElementSize = (pageDom) => {
  const rect = pageDom.getBoundingClientRect();
  const styles = window.getComputedStyle(pageDom);

  const paddingLeft = parseFloat(styles.paddingLeft) || 0;
  const paddingRight = parseFloat(styles.paddingRight) || 0;
  const paddingTop = parseFloat(styles.paddingTop) || 0;
  const paddingBottom = parseFloat(styles.paddingBottom) || 0;

  const cleanWidth = rect.width - paddingLeft - paddingRight;
  const cleanHeight = rect.height - paddingTop - paddingBottom;

  return { width: cleanWidth, height: cleanHeight };
};

const calcNetNodeSize = (query) => {
  const pageDom = findNodeByName({ name: 'PageBody', query })?.dom;
  if (!pageDom) return;

  return cleanCalcElementSize(pageDom);
};

const getNodeAtPath = ({ root, path }) => {
  let n = root;
  for (const i of path) n = n && n.childNodes[i];
  return n || null;
};

const placeCaretAfter = (node) => {
  const selection = window.getSelection();
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

const getBrByPath = (nodeAtPath) => {
  return nodeAtPath?.nodeName === 'BR'
    ? nodeAtPath
    : nodeAtPath?.nextSibling?.nodeName === 'BR'
    ? nodeAtPath.nextSibling
    : null;
};

const formatHtmlForEmail = (rawHtml) => {
  let html = rawHtml
    .replace(/<div[^>]*>/gi, '<br>')
    .replace(/<\/div>/gi, '')
    .replace(/&nbsp;/gi, ' ');

  const kept = [];
  html = html.replace(/\{\{\s*[\w.-]+\s*\}\}/g, (m) => {
    const id = kept.push(m) - 1;
    return `§PH${id}§`;
  });

  html = html.replace(/\{\{\s*[\w.-]*/g, '');
  html = html.replace(/[\w.-]*\s*\}\}/g, '');
  html = html.replace(/[{}]/g, '');
  html = html.replace(/§PH(\d+)§/g, (_, i) => kept[Number(i)]);

  return html;
};

const pathTo = (node, root) => {
  const path = [];
  for (let n = node; n && n !== root; n = n.parentNode) {
    path.push([...n.parentNode.childNodes].indexOf(n));
  }
  return path.reverse();
};

const placeCaretInstead = (rootP) => {
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(rootP);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const getSelectionClosestDiv = (selection) => {
  const selectionStartContainer = selection.getRangeAt(0).startContainer;
  const el = isElementNode(selectionStartContainer)
    ? selectionStartContainer
    : selectionStartContainer.parentElement;
  const div = el?.closest('div');
  return div;
};

const getFlex = ({ flexDirection, flexOrder }) => {
  if (flexOrder !== 'normal') {
    return { flexDirection: `${flexDirection}-${flexOrder}` };
  }

  return { flexDirection };
};

const calcParentSize = ({ query, parentId }) => {
  const parentDom = query.node(parentId).get().dom;

  if (!parentDom) return;

  return cleanCalcElementSize(parentDom);
};

const isValidNum = (num) => {
  if (isNaN(num) || num === 0 || num === null) {
    return false;
  }

  return true;
};

const calcRenderTimes = ({ parentSize, propSize }) => {
  const isParentSizeValid = isValidNum(parentSize);
  const isPropSizeValid = isValidNum(propSize);
  if (isParentSizeValid && isPropSizeValid) {
    return Math.floor(parentSize / propSize);
  }

  return 0;
};

export {
  getSelectionClosestDiv,
  placeCaretInstead,
  pathTo,
  formatHtmlForEmail,
  placeCaretAfter,
  getBrByPath,
  DEFAULT_STYLES,
  applyOrToggleStyle,
  getSelectionStyles,
  getEditorElement,
  isElementNode,
  addDeleteButton,
  getPos,
  createIndicatorDiv,
  createNameDiv,
  stateSelector,
  addDeleteStyles,
  removeDeleteStyles,
  addGridStyles,
  removeGridStyles,
  removeSelectionStyles,
  isDomButton,
  addButtonSelectionStyles,
  addComponentSelectionStyles,
  updateElementPosition,
  addListenerToCraftEditor,
  removeListenerFromCraftEditor,
  createDivAndAddStyles,
  calcPageNumByHeight,
  calcNetNodeSize,
  cleanCalcElementSize,
  getNodeAtPath,
  getFlex,
  findNodeByName,
  calcParentSize,
  isValidNum,
  calcRenderTimes,
};
